import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useAttachEvidenceToActivityMutation,
  useCommitUploadSessionMutation,
  useCreateUploadSessionMutation,
  useDiscardUploadSessionMutation,
  useGetUploadSessionQuery,
  useListUploadSessionsQuery,
  usePromoteSessionFileMutation,
  useRemoveFromSessionMutation,
  useUpdateSessionFileMetadataMutation,
  useUploadToSessionMutation,
} from '../api/emissions';
import {
  UploadSessionStatus,
  type EmissionEvidence,
  type PromoteFileResult,
  type SessionFile,
  type SessionFileMetadataUpdate,
  type UploadSession,
} from '../types/emissions';
import { buildEvidenceStubFromSessionFile } from '../utils/evidenceFromSession';
import { handleRouteError } from '../../../logger';

// Matches the ApiError shape that RTK Query's `.unwrap()` rejects with.
const hasStatus = (err: unknown, status: number): boolean =>
  typeof err === 'object' && err !== null && 'status' in err && (err as { status: unknown }).status === status;

interface UseUploadSessionOptions {
  initiativeId: string;
}

/**
 * Discriminated failure shape so the caller can pick the right retry path:
 *   - `kind: 'promote'` — promote itself threw; file is still on the session
 *     and retry goes through `promoteSelected` again.
 *   - `kind: 'attach'` — evidence was persisted but the activity link failed;
 *     `evidenceId` + `activityId` are present so retry goes through
 *     `retryAttach` (attach-only endpoint) instead of hitting "Cannot modify
 *     a promoted file". Carries `activityId` because a single promote can
 *     fan out to N activities in the multi-activity asset flow; only the
 *     specific link that failed needs retrying.
 */
export type PromoteSelectedFailure =
  | { kind: 'promote'; fileId: string; error: unknown }
  | { kind: 'attach'; fileId: string; evidenceId: string; activityId: string; error: unknown };

export interface PromoteSelectedResult {
  /**
   * Evidence successfully persisted AND linked to the primary activity (or,
   * for library-only uploads with no primary, simply persisted). Files whose
   * primary attach failed are tracked in `failures` and NOT duplicated here
   * — `retryAttach` handles recovery.
   */
  evidences: EmissionEvidence[];
  failures: PromoteSelectedFailure[];
}

export interface UseUploadSessionResult {
  session: UploadSession | null;
  isCreating: boolean;
  isFetching: boolean;
  /** Creates the session on first call, idempotent afterwards. */
  ensureSession: () => Promise<UploadSession>;
  /**
   * Discard the current session (server + client state). The next upload
   * creates a fresh one. No-op if there's no session; rejects if the
   * backend fails (cross-user, committed session, 5xx, etc.).
   */
  startFreshSession: () => Promise<void>;
  /**
   * Commit the current session so the hydrate-on-mount path in the next
   * flow doesn't pick it up and surface already-promoted files with a
   * disabled picker. No-op when there's no session; swallows errors
   * (activity has already been submitted by the time this is called).
   */
  commitSession: () => Promise<void>;
  uploadFile: (file: File, metadata?: SessionFileMetadataUpdate) => Promise<SessionFile>;
  updateMetadata: (fileId: string, metadata: SessionFileMetadataUpdate) => Promise<SessionFile>;
  removeFile: (fileId: string) => Promise<void>;
  promoteFile: (
    fileId: string,
    options?: { activityId?: string; metadata?: SessionFileMetadataUpdate }
  ) => Promise<PromoteFileResult>;
  /**
   * Promote N files and link each resulting evidence to M activities. Plural
   * by design: single-activity callers pass `[id]`, multi-activity callers
   * (asset flow) pass the full list. Empty array = promote only (no attach).
   *
   * For M >= 1, the primary activity is `activityIds[0]` — handled by the
   * promote route itself so we inherit its dedup-collision + attach-failure
   * semantics. Secondary activities are linked via a follow-up attach loop
   * per evidence; failures there surface as `kind: 'attach'` carrying the
   * specific `activityId` that failed.
   */
  promoteSelected: (
    fileIds: string[],
    options?: { activityIds: string[] }
  ) => Promise<PromoteSelectedResult>;
  /**
   * Retry the attach-only path for files whose promote succeeded but whose
   * link failed. Each failure already knows which `activityId` it needs;
   * returns the still-unresolved subset for a further retry.
   */
  retryAttach: (
    failures: Extract<PromoteSelectedFailure, { kind: 'attach' }>[],
  ) => Promise<Extract<PromoteSelectedFailure, { kind: 'attach' }>[]>;
}

/**
 * Manages an upload session for the current data-entry flow. The session is
 * created lazily on the first `ensureSession()` / `uploadFile()` call so
 * opening the form doesn't create empty sessions.
 */
export const useUploadSession = ({ initiativeId }: UseUploadSessionOptions): UseUploadSessionResult => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Sequential uploads inside the same drop-batch run through stale
  // `useCallback` closures — React hasn't flushed `setSessionId` between
  // `await`s, so `sessionId` reads null for every file after the first.
  // The ref captures the created session synchronously so every subsequent
  // `ensureSession` sees it and skips re-creation. Covers both sequential
  // (for-of + await) and concurrent (Promise.all) drop patterns.
  const createdSessionRef = useRef<UploadSession | null>(null);
  const creationInFlight = useRef<Promise<UploadSession> | null>(null);
  // Flag so the one-shot hydrate effect doesn't re-seed after an explicit
  // `startFreshSession()` — without this, discarding the current session
  // would let the next active-sessions refetch pick it up again.
  const hydrationClaimedRef = useRef(false);
  // Dedup ref so repeated error objects from RTK Query refetches (each
  // attempt returns a fresh error instance) don't produce duplicate Sentry
  // events. A genuinely new error (post token refresh, different 5xx) still
  // logs because the identity differs.
  const loggedActiveSessionsErrorRef = useRef<unknown>(null);

  const [createSession, { isLoading: isCreating }] = useCreateUploadSessionMutation();
  const [uploadToSession] = useUploadToSessionMutation();
  const [updateFileMetadata] = useUpdateSessionFileMetadataMutation();
  const [removeFromSession] = useRemoveFromSessionMutation();
  const [promoteFileMutation] = usePromoteSessionFileMutation();
  const [discardSessionMutation] = useDiscardUploadSessionMutation();
  const [commitSessionMutation] = useCommitUploadSessionMutation();
  const [attachEvidence] = useAttachEvidenceToActivityMutation();

  const { data: fetchedSession, isFetching } = useGetUploadSessionQuery(
    sessionId ? { initiativeId, sessionId } : { initiativeId, sessionId: '' },
    { skip: !sessionId }
  );
  // Pin `session` to sessionId — without this, a stale cache read after
  // startFreshSession/commitSession (sessionId flipped to null, but the
  // RTK cache entry for the previous sessionId still holds data) lets the
  // tray keep rendering the cleared files until the tag invalidation
  // refetches. Gating on sessionId makes the clear synchronous.
  const session = sessionId ? (fetchedSession ?? null) : null;

  // Resume the newest active session the user left behind. Sessions live on
  // the server for 7 days, so "drop files → navigate away → come back" keeps
  // the staged files instead of silently starting from scratch.
  const { data: activeSessions, error: activeSessionsError } = useListUploadSessionsQuery(
    { initiativeId, status: UploadSessionStatus.Active },
    { skip: !initiativeId },
  );

  useEffect(() => {
    // List endpoint errored (auth loss, 5xx, network). Log once per distinct
    // error so ops see the failure, but don't claim the hydration slot — a
    // token-refresh retry later in the session should still hydrate. Any
    // user action that creates a session in the meantime claims the slot
    // itself via ensureSession, so a recovered refetch can't hijack.
    if (activeSessionsError) {
      if (loggedActiveSessionsErrorRef.current !== activeSessionsError) {
        loggedActiveSessionsErrorRef.current = activeSessionsError;
        handleRouteError(activeSessionsError, { initiativeId });
      }
      return;
    }
    if (hydrationClaimedRef.current || !activeSessions || activeSessions.length === 0) {
      return;
    }
    const newest = [...activeSessions].sort((a, b) =>
      (b.updated ?? '').localeCompare(a.updated ?? ''),
    )[0];
    hydrationClaimedRef.current = true;
    createdSessionRef.current = newest;
    setSessionId(newest._id);
  }, [activeSessions, activeSessionsError, initiativeId]);

  const ensureSession = useCallback(async (): Promise<UploadSession> => {
    if (createdSessionRef.current) {
      return createdSessionRef.current;
    }
    if (creationInFlight.current) {
      return creationInFlight.current;
    }
    creationInFlight.current = createSession({ initiativeId }).unwrap();
    try {
      const created = await creationInFlight.current;
      createdSessionRef.current = created;
      // Claim the hydration slot so a late active-sessions refetch doesn't
      // swap us onto a different (older) session after we just created a
      // fresh one.
      hydrationClaimedRef.current = true;
      setSessionId(created._id);
      return created;
    } finally {
      creationInFlight.current = null;
    }
  }, [createSession, initiativeId]);

  // commit/discard read the active session from `createdSessionRef`, which
  // is set in lockstep with `setSessionId` everywhere it's assigned. Closing
  // over `sessionId` state here would re-create the callbacks each time a
  // new session is created, breaking consumers (e.g. LibraryUploadModal)
  // whose effects depend on these refs being stable mid-upload.
  const commitSession = useCallback(async (): Promise<void> => {
    const currentId = createdSessionRef.current?._id ?? null;
    createdSessionRef.current = null;
    setSessionId(null);
    hydrationClaimedRef.current = true;
    if (!currentId) {
      return;
    }
    try {
      await commitSessionMutation({ initiativeId, sessionId: currentId }).unwrap();
    } catch (err: unknown) {
      // 400 = session status ≠ Active (already committed or discarded).
      // Idempotent success — cleanup already happened.
      if (hasStatus(err, 400)) {
        return;
      }
      // 5xx / auth / network. Swallow so the activity submit (which has
      // already succeeded) can still exit, but capture the exception so
      // ops see it — next flow's hydrate will surface the stale files
      // unless the user clicks Start fresh.
      handleRouteError(err, { initiativeId, sessionId: currentId });
    }
  }, [commitSessionMutation, initiativeId]);

  const startFreshSession = useCallback(async (): Promise<void> => {
    const currentId = createdSessionRef.current?._id ?? null;
    createdSessionRef.current = null;
    setSessionId(null);
    // Claim the slot so the RTK refetch triggered by discardUploadSession
    // invalidating SESSION_TAG can't re-hydrate the now-cleared session.
    hydrationClaimedRef.current = true;
    if (!currentId) {
      return;
    }
    // Backend's discard is idempotent for already-Expired sessions (returns
    // { discarded: true } rather than an error), so the only thing that
    // reaches here is a genuine failure — cross-user/tenant, committed
    // session, 5xx, auth loss, network drop. Rethrow so the caller toasts.
    await discardSessionMutation({ initiativeId, sessionId: currentId }).unwrap();
  }, [discardSessionMutation, initiativeId]);

  const uploadFile = useCallback(
    async (file: File, metadata?: SessionFileMetadataUpdate): Promise<SessionFile> => {
      const active = await ensureSession();
      return uploadToSession({ initiativeId, sessionId: active._id, file, metadata }).unwrap();
    },
    [ensureSession, initiativeId, uploadToSession]
  );

  // Mirror sessionId into a ref so resolveSessionId stays identity-stable
  // when state changes — see the dep-array fix on commit/discard above for
  // why an unstable identity here would propagate to updateMetadata,
  // removeFile, promoteFile and re-fire any consumer effect that depends
  // on them.
  const sessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Ref reads (written synchronously by ensureSession) cover the window
  // between mutation resolve and React flushing setSessionId — without it,
  // a follow-up mutation right after upload sees the stale closure value
  // and throws "No active session", silently nuking auto-categorise.
  const resolveSessionId = useCallback(
    () => createdSessionRef.current?._id ?? sessionIdRef.current,
    [],
  );

  const updateMetadata = useCallback(
    async (fileId: string, metadata: SessionFileMetadataUpdate): Promise<SessionFile> => {
      const activeId = resolveSessionId();
      if (!activeId) {
        throw new Error('No active session');
      }
      return updateFileMetadata({ initiativeId, sessionId: activeId, fileId, metadata }).unwrap();
    },
    [initiativeId, resolveSessionId, updateFileMetadata]
  );

  const removeFile = useCallback(
    async (fileId: string): Promise<void> => {
      const activeId = resolveSessionId();
      if (!activeId) {
        return;
      }
      await removeFromSession({ initiativeId, sessionId: activeId, fileId }).unwrap();
    },
    [initiativeId, removeFromSession, resolveSessionId]
  );

  const promoteFile = useCallback(
    async (
      fileId: string,
      options: { activityId?: string; metadata?: SessionFileMetadataUpdate } = {}
    ): Promise<PromoteFileResult> => {
      const activeId = resolveSessionId();
      if (!activeId) {
        throw new Error('No active session');
      }
      return promoteFileMutation({
        initiativeId,
        sessionId: activeId,
        fileId,
        activityId: options.activityId,
        metadata: options.metadata,
      }).unwrap();
    },
    [initiativeId, promoteFileMutation, resolveSessionId]
  );

  const promoteSelected = useCallback(
    async (
      fileIds: string[],
      options: { activityIds: string[] } = { activityIds: [] },
    ): Promise<PromoteSelectedResult> => {
      const [primaryActivityId, ...secondaryActivityIds] = options.activityIds;

      // Split selection by promotion state. Already-promoted files
      // (re-selected from the session's history) skip the promote step
      // entirely and go straight to attach-only against every activity —
      // the evidence doc already exists in the same initiative, so
      // re-using it is a one-call-per-link link-up.
      const sessionFiles = createdSessionRef.current?.files ?? [];
      const filesById = new Map(sessionFiles.map((f) => [f.fileId, f]));
      const toPromote: string[] = [];
      const toReattach: { file: SessionFile; evidenceId: string }[] = [];
      for (const fileId of fileIds) {
        const file = filesById.get(fileId);
        if (file?.promotedToEvidenceId) {
          toReattach.push({ file, evidenceId: file.promotedToEvidenceId });
        } else {
          toPromote.push(fileId);
        }
      }

      // Promotions are independent server-side; run them concurrently so a
      // batch of N files completes in ~one round-trip instead of N. Primary
      // activityId (if any) rides along on the promote route so we inherit
      // its atomic create-evidence-and-attach semantics.
      const outcomes = await Promise.all(
        toPromote.map(async (fileId) => {
          const result = await promoteFile(fileId, { activityId: primaryActivityId }).catch(
            (error: unknown) => ({ fileId, error } as const),
          );
          return { fileId, result } as const;
        }),
      );

      const evidences: EmissionEvidence[] = [];
      const failures: PromoteSelectedFailure[] = [];
      for (const { fileId, result } of outcomes) {
        if ('error' in result) {
          failures.push({ kind: 'promote', fileId, error: result.error });
          continue;
        }
        if (primaryActivityId && !result.attached) {
          // Evidence persisted but the primary activity link failed.
          // Don't push to `evidences` — it's reserved for fully-linked
          // evidence the UI echoes back as "attached". The evidence is
          // still recoverable via `retryAttach`.
          failures.push({
            kind: 'attach',
            fileId,
            evidenceId: result.evidence._id,
            activityId: primaryActivityId,
            error: new Error(result.attachError ?? 'Failed to attach evidence to activity'),
          });
          continue;
        }
        evidences.push(result.evidence);
      }

      // Secondary attach loop — for the asset flow, the same evidence doc
      // links to every created activity. Primary is handled atomically by
      // promote; these tail links go through the attach-only endpoint
      // (idempotent server-side).
      if (secondaryActivityIds.length > 0) {
        const secondaryOutcomes = await Promise.all(
          outcomes.flatMap(({ fileId, result }) => {
            if ('error' in result) {
              return [];
            }
            return secondaryActivityIds.map(async (activityId) => {
              const error = await attachEvidence({
                initiativeId,
                evidenceId: result.evidence._id,
                activityId,
              })
                .unwrap()
                .then(() => null)
                .catch((err: unknown) => err);
              return { fileId, evidenceId: result.evidence._id, activityId, error };
            });
          }),
        );
        for (const { fileId, evidenceId, activityId, error } of secondaryOutcomes) {
          if (error !== null) {
            failures.push({ kind: 'attach', fileId, evidenceId, activityId, error });
          }
        }
      }

      // Re-attach path for already-promoted files. One attach call per
      // (file, activity) link; on full success the file surfaces in
      // `evidences` as a lightweight stub built from the SessionFile so
      // the result step can render it alongside freshly-promoted rows.
      if (toReattach.length > 0 && options.activityIds.length > 0) {
        const reattachOutcomes = await Promise.all(
          toReattach.flatMap(({ file, evidenceId }) =>
            options.activityIds.map(async (activityId) => {
              const error = await attachEvidence({ initiativeId, evidenceId, activityId })
                .unwrap()
                .then(() => null)
                .catch((err: unknown) => err);
              return { file, evidenceId, activityId, error };
            }),
          ),
        );
        const failedFileIds = new Set<string>();
        for (const { file, evidenceId, activityId, error } of reattachOutcomes) {
          if (error !== null) {
            failedFileIds.add(file.fileId);
            failures.push({ kind: 'attach', fileId: file.fileId, evidenceId, activityId, error });
          }
        }
        for (const { file, evidenceId } of toReattach) {
          if (failedFileIds.has(file.fileId)) {
            continue;
          }
          evidences.push(buildEvidenceStubFromSessionFile(file, evidenceId, initiativeId));
        }
      }

      return { evidences, failures };
    },
    [attachEvidence, initiativeId, promoteFile],
  );

  const retryAttach = useCallback(
    async (
      failures: Extract<PromoteSelectedFailure, { kind: 'attach' }>[],
    ): Promise<Extract<PromoteSelectedFailure, { kind: 'attach' }>[]> => {
      const outcomes = await Promise.all(
        failures.map(async (failure) => {
          const error = await attachEvidence({
            initiativeId,
            evidenceId: failure.evidenceId,
            activityId: failure.activityId,
          })
            .unwrap()
            .then(() => null)
            .catch((err: unknown) => err);
          return { failure, error };
        }),
      );
      return outcomes
        .filter((o) => o.error !== null)
        .map((o) => ({ ...o.failure, error: o.error }));
    },
    [attachEvidence, initiativeId],
  );

  return {
    session,
    isCreating,
    isFetching,
    ensureSession,
    startFreshSession,
    commitSession,
    uploadFile,
    updateMetadata,
    removeFile,
    promoteFile,
    promoteSelected,
    retryAttach,
  };
};
